/**
 * 指针拖拽排序（鼠标 / 触摸 / 触控笔通用）
 *
 * 用法：列表渲染完之后调一次 enableSortable(ul, (from, to) => 把 state 里的元素挪一下)
 * 每次重渲染重复调用是安全的 —— 同一个元素只绑一次监听，后续调用只更新回调。
 *
 * 为什么不用原生 HTML5 draggable：
 * - 触屏（iOS / Android）上 draggable 完全不生效，而「图片转 PDF」的主战场正是手机相册
 * - 原生拖影在可滚动列表里定位不准，也没法自定义样式
 *
 * 交互约定：
 * - 鼠标：整行任意位置都能起手（按住 ✕ 删除按钮除外），移动超过 4px 才算拖拽，
 *   所以「点一下」不会被误判
 * - 触摸：只能从 ⋮⋮ 拖柄起手（.handle 上设了 touch-action: none），
 *   这样列表自身的上下滚动不会被拖拽抢走
 */
export type SortHandler = (from: number, to: number) => void;

interface SortState {
  onReorder: SortHandler;
}

/** 起手移动超过这个像素才判定为拖拽，避免把点击吃成拖动 */
const THRESHOLD = 4;

/** 已绑定的列表 → 状态。用 WeakMap 让重渲染时可以只换回调、不叠加监听器 */
const REGISTRY = new WeakMap<HTMLElement, SortState>();

export function enableSortable(listEl: HTMLElement, onReorder: SortHandler) {
  const existing = REGISTRY.get(listEl);
  if (existing) {
    existing.onReorder = onReorder;
    return;
  }
  REGISTRY.set(listEl, bind(listEl, onReorder));
}

function bind(listEl: HTMLElement, onReorder: SortHandler): SortState {
  const state: SortState = { onReorder };

  let dragEl: HTMLElement | null = null;
  let fromIndex = -1;
  let startX = 0;
  let startY = 0;
  let pointerId = -1;
  let active = false;

  const indexOf = (el: Element) => Array.prototype.indexOf.call(listEl.children, el) as number;

  /** 指针纵向位置 → 应该插到哪个兄弟节点之前（null 表示插到末尾） */
  function slotAt(y: number): HTMLElement | null {
    const others = Array.from(listEl.children).filter((el) => el !== dragEl) as HTMLElement[];
    for (const sib of others) {
      const r = sib.getBoundingClientRect();
      if (y < r.top + r.height / 2) return sib;
    }
    return null;
  }

  function reset() {
    if (dragEl) {
      dragEl.classList.remove('dragging');
      try {
        if (pointerId >= 0 && dragEl.hasPointerCapture(pointerId)) dragEl.releasePointerCapture(pointerId);
      } catch {
        /* 指针已消失时释放会抛错，忽略 */
      }
    }
    listEl.classList.remove('sorting');
    dragEl = null;
    fromIndex = -1;
    pointerId = -1;
    active = false;
  }

  function finish(commit: boolean) {
    const el = dragEl;
    if (!el || !active) return reset();
    const to = indexOf(el);
    const from = fromIndex;
    const cb = state.onReorder;
    reset();
    if (commit && from >= 0 && to >= 0 && to !== from) cb(from, to);
  }

  listEl.addEventListener('pointerdown', (e) => {
    if (pointerId >= 0) return; // 已有一根手指/指针在拖，第二根不抢
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const target = e.target as HTMLElement;
    // 行内的按钮（✕ 删除）保持原样，不进拖拽流程
    if (target.closest('button')) return;
    const li = target.closest('li');
    if (!li || li.parentElement !== listEl) return;
    // 触摸必须从拖柄起手，否则和列表滚动打架
    if (e.pointerType !== 'mouse' && !target.closest('.handle')) return;
    dragEl = li as HTMLElement;
    fromIndex = indexOf(li);
    startX = e.clientX;
    startY = e.clientY;
    pointerId = e.pointerId;
    active = false;
  });

  listEl.addEventListener('pointermove', (e) => {
    if (!dragEl || e.pointerId !== pointerId) return;
    if (!active) {
      if (Math.hypot(e.clientX - startX, e.clientY - startY) < THRESHOLD) return;
      active = true;
      try {
        dragEl.setPointerCapture(pointerId);
      } catch {
        /* 合成事件可能没有可捕获的指针，退化为普通冒泡 */
      }
      dragEl.classList.add('dragging');
      listEl.classList.add('sorting');
    }
    e.preventDefault();
    const ref = slotAt(e.clientY);
    // 只在位置真的变了才动 DOM，避免每帧都重排
    if (dragEl.nextElementSibling !== ref) listEl.insertBefore(dragEl, ref);
  });

  listEl.addEventListener('pointerup', (e) => {
    if (e.pointerId === pointerId) finish(true);
  });

  // 指针被系统取消（来电、手势切换等）时也落定：DOM 已经挪过了，
  // 此时回滚反而会和 state 不一致，直接按最终位置提交更安全
  listEl.addEventListener('pointercancel', (e) => {
    if (e.pointerId === pointerId) finish(true);
  });

  return state;
}
